/**
 * Content Editor Component
 * 
 * Handles text content editing for both single posts and threads.
 * Provides real-time character counting and platform-specific validation.
 */

import React from 'react';
import {
    Box,
    TextField,
    Typography,
    LinearProgress,
    Alert,
    Button,
    IconButton,
    Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { getPlatformLimits, getPlatformDisplayName } from '../../utils/platformUtils';

// Styled Components
const CharacterCounter = styled(Box)(({ theme, overLimit }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(1),
    color: overLimit ? theme.palette.error.main : theme.palette.text.secondary,
    fontSize: '0.875rem'
}));

const ThreadPostContainer = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    position: 'relative'
}));

const ThreadControls = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1)
}));

const ContentEditor = ({
    content,
    onContentChange,
    postType,
    threadData,
    onThreadDataChange,
    selectedPlatforms,
    disabled = false
}) => {
    // Get the most restrictive character limit from selected platforms
    const getCharacterLimit = () => {
        if (selectedPlatforms.length === 0) return 280; // Default to Twitter limit
        
        return Math.min(
            ...selectedPlatforms.map(platform => {
                const limits = getPlatformLimits(platform.platform);
                return limits.maxLength;
            })
        );
    };

    const characterLimit = getCharacterLimit();
    const currentLength = content.length;
    const isOverLimit = currentLength > characterLimit;
    const progressValue = (currentLength / characterLimit) * 100;

    // Thread handlers
    const addThreadPost = () => {
        const newPost = { text: '', mediaUrl: '', altText: '' };
        onThreadDataChange([...threadData, newPost]);
    };

    const removeThreadPost = (index) => {
        if (threadData.length <= 2) return; // Minimum 2 posts for thread
        const updated = threadData.filter((_, i) => i !== index);
        onThreadDataChange(updated);
    };

    const updateThreadPost = (index, field, value) => {
        const updated = threadData.map((post, i) => 
            i === index ? { ...post, [field]: value } : post
        );
        onThreadDataChange(updated);
    };

    // Initialize thread data if needed
    React.useEffect(() => {
        if (postType === 'thread' && threadData.length === 0) {
            onThreadDataChange([
                { text: '', mediaUrl: '', altText: '' },
                { text: '', mediaUrl: '', altText: '' }
            ]);
        }
    }, [postType, threadData.length, onThreadDataChange]);

    if (postType === 'thread') {
        return (
            <Box>
                <Typography variant="subtitle2" gutterBottom>
                    Thread Content ({threadData.length} posts)
                </Typography>
                
                {threadData.map((post, index) => (
                    <ThreadPostContainer key={index} elevation={1}>
                        <ThreadControls>
                            <Typography variant="caption" color="text.secondary">
                                Post {index + 1}
                            </Typography>
                            {threadData.length > 2 && (
                                <IconButton
                                    size="small"
                                    onClick={() => removeThreadPost(index)}
                                    disabled={disabled}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            )}
                        </ThreadControls>
                        
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            maxRows={6}
                            placeholder={`What's happening in post ${index + 1}?`}
                            value={post.text}
                            onChange={(e) => updateThreadPost(index, 'text', e.target.value)}
                            disabled={disabled}
                            variant="outlined"
                        />
                        
                        <CharacterCounter overLimit={post.text.length > characterLimit}>
                            <Typography variant="caption">
                                {selectedPlatforms.length > 0 && (
                                    `Character limits: ${selectedPlatforms.map(p => 
                                        `${getPlatformDisplayName(p.platform)}: ${getPlatformLimits(p.platform).maxLength}`
                                    ).join(', ')}`
                                )}
                            </Typography>
                            <Typography variant="caption">
                                {post.text.length}/{characterLimit}
                            </Typography>
                        </CharacterCounter>
                        
                        {post.text.length > characterLimit && (
                            <LinearProgress 
                                variant="determinate" 
                                value={Math.min((post.text.length / characterLimit) * 100, 100)}
                                color="error"
                                sx={{ mt: 1 }}
                            />
                        )}
                    </ThreadPostContainer>
                ))}
                
                <Button
                    startIcon={<AddIcon />}
                    onClick={addThreadPost}
                    disabled={disabled || threadData.length >= 25} // Twitter limit
                    variant="outlined"
                    size="small"
                >
                    Add Post to Thread
                </Button>
                
                {threadData.length >= 25 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        Maximum thread length reached (25 posts)
                    </Alert>
                )}
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="subtitle2" gutterBottom>
                Post Content
            </Typography>
            
            <TextField
                fullWidth
                multiline
                minRows={4}
                maxRows={8}
                placeholder="What's happening?"
                value={content}
                onChange={(e) => onContentChange(e.target.value)}
                disabled={disabled}
                variant="outlined"
                error={isOverLimit}
                helperText={isOverLimit ? 'Content exceeds character limit' : ''}
            />
            
            <CharacterCounter overLimit={isOverLimit}>
                <Box>
                    {selectedPlatforms.length > 0 && (
                        <Typography variant="caption" display="block">
                            Character limits: {selectedPlatforms.map(platform => 
                                `${getPlatformDisplayName(platform.platform)}: ${getPlatformLimits(platform.platform).maxLength}`
                            ).join(', ')}
                        </Typography>
                    )}
                </Box>
                <Typography variant="caption">
                    {currentLength}/{characterLimit}
                </Typography>
            </CharacterCounter>
            
            <LinearProgress 
                variant="determinate" 
                value={Math.min(progressValue, 100)}
                color={isOverLimit ? 'error' : progressValue > 80 ? 'warning' : 'primary'}
                sx={{ mt: 1, height: 4, borderRadius: 2 }}
            />
            
            {isOverLimit && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    Content exceeds the character limit for some platforms. 
                    Consider shortening your message or removing platforms with stricter limits.
                </Alert>
            )}
            
            {selectedPlatforms.length > 1 && (
                <Alert severity="info" sx={{ mt: 1 }}>
                    Using the most restrictive character limit ({characterLimit}) from your selected platforms.
                </Alert>
            )}
        </Box>
    );
};

export default ContentEditor; 